import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, ChevronUp, ChevronDown, Info } from 'lucide-react';

const StrategySetSection = ({ strategySet, setStrategySet }) => {
  const [showRuntimeEntry, setShowRuntimeEntry] = useState(false);
  const [showRuntimeOvernight, setShowRuntimeOvernight] = useState(false);
  const [showUniversalExit, setShowUniversalExit] = useState(true);

  const addPosition = (type) => {
    const newPosition = {
      buy_sell: 'Buy',
      underlying: 'NIFTY',
      strike: '',
      type: 'CE',
      expiry: '',
      qty: 1,
      price: 'Market'
    };

    setStrategySet(prev => ({
      ...prev,
      [`${type}_positions`]: [...(prev[`${type}_positions`] || []), newPosition]
    }));
  };

  const removePosition = (type, index) => {
    setStrategySet(prev => ({
      ...prev,
      [`${type}_positions`]: prev[`${type}_positions`].filter((_, i) => i !== index)
    }));
  };

  const updatePosition = (type, index, field, value) => {
    setStrategySet(prev => ({
      ...prev,
      [`${type}_positions`]: prev[`${type}_positions`].map((pos, i) =>
        i === index ? { ...pos, [field]: value } : pos
      )
    }));
  };

  const updateSection = (section, field, value) => {
    setStrategySet(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  return (
    <Card className="p-6 bg-white/80 backdrop-blur-sm border-gray-200" data-testid="strategy-set-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h3 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Set №1
          </h3>
          <Select defaultValue="no-list">
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="no-list">No list selected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm">
            <Info className="w-4 h-4" />
          </Button>
        </div>
        <Input placeholder="Description" className="max-w-md" data-testid="set-description" />
      </div>

      {/* Entry Section */}
      <div className="mb-6 p-6 bg-slate-50 rounded-xl border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h4 className="text-lg font-semibold text-gray-900">Entry <span className="text-gray-500 text-sm">(STE)</span></h4>
            <Button variant="ghost" size="sm">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowRuntimeEntry(!showRuntimeEntry)}
            className="text-indigo-600"
          >
            Runtime Variable {showRuntimeEntry ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </Button>
        </div>

        <Input
          placeholder="Description"
          value={strategySet.entry?.description || ''}
          onChange={(e) => updateSection('entry', 'description', e.target.value)}
          className="mb-4"
          data-testid="entry-description"
        />

        <div className="grid md:grid-cols-2 gap-6">
          {/* CONDITIONS */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h5 className="text-sm font-semibold text-gray-700">CONDITIONS</h5>
              <Button variant="link" size="sm" className="text-indigo-600 h-auto p-0">
                +Add
              </Button>
            </div>
            <Textarea
              placeholder="Enter conditions..."
              value={strategySet.entry?.conditions_text || ''}
              onChange={(e) => updateSection('entry', 'conditions_text', e.target.value)}
              rows={5}
              className="bg-white"
              data-testid="entry-conditions"
            />
          </div>

          {/* POSITIONS */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h5 className="text-sm font-semibold text-gray-700">POSITIONS</h5>
              <Button
                variant="link"
                size="sm"
                className="text-indigo-600 h-auto p-0"
                onClick={() => addPosition('entry')}
                data-testid="add-entry-position"
              >
                +Add
              </Button>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="grid grid-cols-8 gap-2 text-xs text-gray-600 mb-2">
                <div>Buy/Sell</div>
                <div>Underlying</div>
                <div>Strike</div>
                <div>Type</div>
                <div>Expiry</div>
                <div>Qty</div>
                <div>Price</div>
                <div></div>
              </div>
              {strategySet.entry_positions?.map((pos, idx) => (
                <PositionRow
                  key={idx}
                  position={pos}
                  onUpdate={(field, value) => updatePosition('entry', idx, field, value)}
                  onRemove={() => removePosition('entry', idx)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Repair Section */}
      <div className="mb-6 p-6 bg-slate-50 rounded-xl border border-gray-200">
        <div className="flex items-center gap-2 mb-3">
          <h4 className="text-lg font-semibold text-gray-900">Repair</h4>
          <Button variant="link" size="sm" className="text-indigo-600 h-auto p-0">
            +Add
          </Button>
        </div>
        <p className="text-sm text-gray-500">Click +Add to configure repair conditions</p>
      </div>

      {/* Overnight Protection Section */}
      <div className="mb-6 p-6 bg-slate-50 rounded-xl border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h4 className="text-lg font-semibold text-gray-900">Overnight Protection</h4>
            <Button variant="ghost" size="sm">
              <Info className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowRuntimeOvernight(!showRuntimeOvernight)}
            className="text-indigo-600"
          >
            Runtime Variable {showRuntimeOvernight ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </Button>
        </div>
        <Input placeholder="Description" className="bg-white" data-testid="overnight-description" />
      </div>

      {/* Universal Exit Section */}
      <div className="p-6 bg-slate-50 rounded-xl border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900">Universal Exit <span className="text-gray-500 text-sm">(U EX)</span></h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowUniversalExit(!showUniversalExit)}
          >
            {showUniversalExit ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>

        {showUniversalExit && (
          <>
            <Input placeholder="Description" className="mb-4 bg-white" data-testid="exit-description" />
            <div>
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-sm font-semibold text-gray-700">CONDITIONS</h5>
                <Button variant="link" size="sm" className="text-indigo-600 h-auto p-0">
                  +Add
                </Button>
              </div>
              <Textarea
                placeholder="Exit all open positions in this strategy"
                rows={5}
                className="bg-white"
                data-testid="exit-conditions"
              />
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

const PositionRow = ({ position, onUpdate, onRemove }) => (
  <div className="grid grid-cols-8 gap-2 mb-2 items-center">
    <Select value={position.buy_sell} onValueChange={(v) => onUpdate('buy_sell', v)}>
      <SelectTrigger className="h-8 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="Buy">Buy</SelectItem>
        <SelectItem value="Sell">Sell</SelectItem>
      </SelectContent>
    </Select>
    <Select value={position.underlying} onValueChange={(v) => onUpdate('underlying', v)}>
      <SelectTrigger className="h-8 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="NIFTY">NIFTY</SelectItem>
        <SelectItem value="BANKNIFTY">BANKNIFTY</SelectItem>
      </SelectContent>
    </Select>
    <Input
      value={position.strike}
      onChange={(e) => onUpdate('strike', e.target.value)}
      placeholder="Strike"
      className="h-8 text-xs"
    />
    <Select value={position.type} onValueChange={(v) => onUpdate('type', v)}>
      <SelectTrigger className="h-8 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="CE">CE</SelectItem>
        <SelectItem value="PE">PE</SelectItem>
      </SelectContent>
    </Select>
    <Input
      value={position.expiry}
      onChange={(e) => onUpdate('expiry', e.target.value)}
      placeholder="Expiry"
      className="h-8 text-xs"
    />
    <Input
      type="number"
      value={position.qty}
      onChange={(e) => onUpdate('qty', parseInt(e.target.value) || 1)}
      className="h-8 text-xs"
    />
    <Input
      value={position.price}
      onChange={(e) => onUpdate('price', e.target.value)}
      placeholder="Market"
      className="h-8 text-xs"
    />
    <Button
      variant="ghost"
      size="sm"
      onClick={onRemove}
      className="h-8 w-8 p-0"
    >
      <Trash2 className="w-3 h-3 text-red-500" />
    </Button>
  </div>
);

export default StrategySetSection;
